// ---------------------------------------------------------------------------
// Conundrum play. The handoff never drew this screen, so it is composed from
// the same parts as the letters screens on purpose: the same tile row, the same
// clock, the same identity chips. A player arriving here should recognise the
// game rather than learn a new one.
//
// The design decisions that are not free:
//
//   1. A buzz really pauses the main clock, server side. So the main clock has
//      to render frozen for everyone who is not answering, and `frozen` is
//      passed into `useClock` rather than inferred: a clock that keeps draining
//      while it is actually paused is the worst bug this screen can have, and
//      the player it robs has no way to know.
//   2. The buzzing player gets the orange ten-second window and the only text
//      input in the room. Nobody else gets an input at all, because nobody else
//      can submit.
//   3. A wrong answer is final. The locked-out player gets a spent state with no
//      retry affordance, because there is no retry: the round continues without
//      them.
//   4. The buzz notice is worded so it cannot be mistaken for the server-pause
//      overlay. Those two must never read alike.
//   5. The disc never moves. A buzz puts a small held clock beside it, so the
//      space that clock will occupy is reserved on both sides of the main disc
//      for the whole round and the geometry is the same before and after. The
//      block below the board holds a floor height for the same reason: the
//      board must not walk up the screen when somebody buzzes.
//
//      THERE IS ONE EXCEPTION, and it is about the screen rather than about the
//      round. On a phone the buzzing player's field takes focus, the OS keyboard
//      covers about half the display, and the discs are the reason the nine
//      letters end up behind it — a player with ten seconds to find an anagram,
//      unable to see the letters. So when the visual viewport says something
//      that size is covering the screen, and only for the player holding the
//      buzz, the two discs become a compact readout and the round strip goes.
//      Nothing else moves and nothing is taken away.
//
//      It is driven by `useVisualViewport` and never by the buzz, which is what
//      keeps the rule above true: a desktop, or a phone with a hardware
//      keyboard, sees exactly the layout it always did, and a buzz on its own
//      still rearranges nothing.
//   6. The nine tiles scale to the column rather than overflowing it. They are
//      nine by definition and `board` tiles are wider than this layout's main
//      column at 900px, which is where the fit exists to be used.
//
// The chat is not here. It is the room shell's, mounted once for the whole
// room, and a second copy on this screen would be a second conversation.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { CONUNDRUM_POINTS } from '@countdown/shared';

/**
 * How close to the end of the answer window, in whole seconds, the draft stops
 * being throttled.
 *
 * Comfortably wider than `DRAFT_DEBOUNCE_MS`, because the point is that no
 * keystroke can still be sitting in the debounce when the server reads the
 * draft at expiry. Read off `seconds` and not `remaining` — `remaining` is the
 * 0-to-1 fraction the pie is drawn from, so comparing it against milliseconds
 * is true at every reading and flushes on every keystroke of the round.
 */
const DRAFT_FLUSH_BEFORE_SECONDS = 2;
import type { ClientRound, PublicPlayer } from '@countdown/shared';
import {
  CompactClock,
  ConnectionStatus,
  LetterTile,
  Padlock,
  LiveClock,
  PlayerChip,
  RoundTypeChip,
  StandingsStrip,
  Button,
  tileFitStyle,
} from '../../components/primitives/index.js';
import { consumeTiles } from '../../lib/consumeTiles.js';
import { useClock } from '../../hooks/useClock.js';
import { useKeyboardMode } from '../../hooks/useKeyboardMode.js';
import { useIsDesktop } from '../../hooks/useMediaQuery.js';
import { useVisualViewport } from '../../hooks/useVisualViewport.js';
import { playerById, seatedPlayers } from '../../lib/playerLookup.js';
import { serverNow, useGameStore } from '../../store/gameStore.js';
import type { RoomScreenProps } from './types.js';
import { isKeyHandlingControl } from '../../lib/isEditable.js';


type PlayerState = 'buzzed' | 'locked out' | 'solved' | 'waiting';

function PlayerRow({ player, status }: { player: PublicPlayer; status: PlayerState }): ReactElement {
  const tone =
    status === 'buzzed'
      ? 'border-accent bg-accent/15 text-accent'
      : status === 'solved'
        ? 'border-good bg-good/15 text-good'
        : status === 'locked out'
          ? 'border-line bg-surface-2 text-ink-dim opacity-70'
          : 'border-line bg-surface-2 text-ink-dim';

  return (
    <div
      data-testid={`player-${player.id}`}
      className={`flex flex-1 flex-col items-center gap-[6px] rounded-tile border p-[10px] transition-colors duration-300 ease-out min-[900px]:flex-row min-[900px]:gap-[10px] min-[900px]:px-[13px] min-[900px]:py-[11px] ${tone}`}
    >
      <span
        className="hidden h-2 w-2 shrink-0 rounded-full min-[900px]:block"
        style={player.color === null ? undefined : { background: player.color }}
        aria-hidden="true"
      />
      {/* `max-w-full min-w-0` for the reason set out on `LettersPlayScreen`'s
          own `PlayerRow`: this row is a column on a phone and a row above 900,
          and a bare `truncate` clamps under neither. */}
      <span className="max-w-full min-w-0 truncate text-[13px] font-semibold text-ink min-[900px]:text-[15px]">
        {player.displayName}
      </span>
      <span className="shrink-0 font-mono text-[10px] font-semibold tracking-[0.08em] uppercase min-[900px]:ml-auto">
        {status}
      </span>
    </div>
  );
}

export function ConundrumPlayScreen({ state, send, connection }: RoomScreenProps): ReactElement {
  const round: ClientRound | undefined = state.rounds[state.currentRoundIndex];
  const quality = useGameStore((store) => store.quality);
  const isDesktop = useIsDesktop();
  const keyboard = useKeyboardMode();
  const viewport = useVisualViewport();
  const [draft, setDraft] = useState('');

  const buzzedBy = round?.buzzedBy ?? null;
  const lockedOut = round?.lockedOut ?? [];
  const solvedBy = round?.solvedBy ?? null;
  const youHoldTheBuzz = buzzedBy !== null && buzzedBy === state.youId;
  const someoneElseBuzzed = buzzedBy !== null && buzzedBy !== state.youId;
  const youAreLockedOut = lockedOut.includes(state.youId);
  const youArePlaying =
    state.players.find((player) => player.id === state.youId)?.role === 'player';

  const main = useClock(round?.clock ?? null, {
    // Both routes to a stopped clock, passed in rather than inferred.
    frozen: state.serverPaused || someoneElseBuzzed,
  });
  const buzzWindow = useClock(round?.buzzClock ?? null, { frozen: state.serverPaused });

  // --- keeping the server's copy of the answer -----------------------------
  //
  // An expired buzz used to be scored as an empty string, because nothing sent
  // the answer until the player pressed submit. So a player who typed the right
  // word and was a moment slow was scored as having typed nothing, and locked
  // out of the round for it. The server now falls back to this draft.
  useEffect(() => {
    if (!youHoldTheBuzz) return;
    connection.streamConundrum(draft);
  }, [draft, youHoldTheBuzz, connection]);

  // The stream is trailing-edge throttled, so the last keystroke before the
  // window shuts can still be in the debounce when the server reads the draft —
  // and that keystroke is exactly the one this whole mechanism exists to save.
  // Once the window is nearly up, push it out on every change instead.
  useEffect(() => {
    if (!youHoldTheBuzz) return;
    if (buzzWindow.seconds > DRAFT_FLUSH_BEFORE_SECONDS) return;
    connection.flushDrafts();
  }, [draft, buzzWindow.seconds, youHoldTheBuzz, connection]);

  const canBuzz =
    round !== undefined &&
    buzzedBy === null &&
    solvedBy === null &&
    !youAreLockedOut &&
    youArePlaying &&
    !main.expired &&
    !state.serverPaused;

  useEffect(() => {
    if (!canBuzz || keyboard.mode !== 'answer') return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== ' ' && event.key !== 'Spacebar') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // SPACE STANDS ASIDE FOR WHATEVER HAS FOCUS, AND THAT IS THE OPPOSITE OF
      // WHAT THE LETTERS SCREEN DOES WITH ENTER. READ BOTH BEFORE CHANGING
      // EITHER.
      //
      // The collision is the same one in both places: a click leaves focus on
      // the button it pressed, on Chrome and on Windows, whether the player
      // meant it or not. So the next activation key goes to a control nobody
      // chose, and the window listener and that control both want it.
      // `LettersPlayScreen` resolves it by taking Enter in every case and
      // cancelling the button's activation — the deference there was making
      // Enter repeat the last press (Shuffle shuffling again, Back backspacing
      // again) instead of ending the word, and its comment argues that out at
      // length.
      //
      // It comes out the other way here because the costs are not alike.
      // Enter on the letters screen is the frequent action, it is repeatable,
      // and getting it wrong costs one keystroke of a thirty-second round.
      // Space here is the one buzz a player has: `buzz()` refuses the second
      // buzzer outright, the answer window that follows is ten seconds, and a
      // wrong word is a lockout for the rest of the conundrum. A player who
      // tapped anything on this screen — Buzz itself, a chip, the header's own
      // controls — and then pressed Space has not asked to spend that, and
      // there is nothing to undo it with. The rule is that the cheaper mistake
      // wins the key, so on this screen the focused control does.
      //
      // The buzz is not lost by it. Nothing on this screen takes focus of its
      // own accord until the buzz is already won, a desktop player types at the
      // board with focus on the body, and the one control this could plausibly
      // stand aside for is the Buzz button, which a Space press activates
      // anyway. `event.defaultPrevented` is honoured for the same reason
      // `LettersPlayScreen` honours it: a control that answers this key with a
      // handler of its own says so by cancelling the default, and that press is
      // already spoken for.
      if (event.defaultPrevented) return;
      if (isKeyHandlingControl(event.target)) return;
      event.preventDefault();
      send({
        type: 'buzz',
        actionId: crypto.randomUUID(),
        // The client's own epoch, the same as `ping` sends. The server pairs it
        // with its own round-trip estimate, so a raw reading is what it wants.
        clientTime: Date.now(),
      });
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canBuzz, keyboard.mode, send]);

  // Unreachable through RoomPage, which routes here by phase. It exists because
  // the phase router's screens must always return an element.
  if (round === undefined) {
    return <p className="p-6 text-ink-dim">Waiting for the round.</p>;
  }

  const scramble = round.scramble ?? '';
  const letters = scramble.split('');

  // Which tiles the answer being typed has spent.
  //
  // A conundrum is worked by elimination — you hold the letters you have used
  // and look at what is left — and the box gave no way to do that, so a player
  // was re-reading their own typing against nine tiles on every keystroke.
  //
  // Only the buzzer is typing, so only the buzzer sees this. `unmatched` is what
  // the board cannot supply; it is worth saying before they submit, because a
  // wrong word here is a lockout rather than a nought.
  const spent = consumeTiles(letters, draft);
  const spentTiles = new Set(spent.used);
  const buzzer = playerById(state, buzzedBy);
  const solver = playerById(state, solvedBy);
  // Everyone, including you. A list of "the others" made you look up your own
  // state somewhere else on the screen to answer the one question the list is
  // for, which here is who is still in the round and who has already spent their
  // buzz. The same change was made on the letters and numbers screens.
  //
  // Unlike those two, your own row has no local reading to prefer: every state
  // this list can show is a server ruling. `buzz()` refuses the second buzzer
  // outright, so a row that lit up the instant you pressed would be claiming a
  // buzz the server may be about to give somebody else — and a lockout is
  // decided by judging a word, not by sending it.
  const roster = seatedPlayers(state);
  // Only the buzzer has a keyboard up, so only the buzzer's screen is covered.
  // Read off the viewport rather than off the buzz: the buzz is not what takes
  // the room, the keyboard is, and a desktop buzzer loses nothing.
  const covered = !isDesktop && youHoldTheBuzz && viewport.keyboardLikely;
  const clockSize = isDesktop ? 200 : 128;
  const tileSize = isDesktop ? 'board' : 'phone';
  const boardGap = isDesktop ? 9 : 5;

  const buzz = () => {
    send({ type: 'buzz', actionId: crypto.randomUUID(), clientTime: Date.now() });
  };

  const submit = () => {
    const answer = draft.trim();
    if (answer.length === 0) return;
    send({
      type: 'submit-conundrum',
      answer,
      actionId: crypto.randomUUID(),
      clientLockedAt: serverNow(),
    });
    setDraft('');
  };

  const statusOf = (player: PublicPlayer): PlayerState => {
    if (player.id === solvedBy) return 'solved';
    if (player.id === buzzedBy) return 'buzzed';
    if (lockedOut.includes(player.id)) return 'locked out';
    return 'waiting';
  };

  return (
    <div className="flex min-h-full flex-col">
      {/* The round strip, not a second header. See the note on the same row in
          LettersPlayScreen: `AppHeader` above it owns the page's only rule, and a
          `border-b` here stacked a second bar under the first. */}
      {/* The standing is what the round looks like from outside it, and the
          player with ten seconds to find an anagram is not outside it. */}
      <header
        className={`items-center gap-2 px-4 py-4 min-[900px]:flex min-[900px]:px-6 ${
          covered ? 'hidden' : 'flex'
        }`}
      >
        <RoundTypeChip type={round.type} size="sm" />
        <div className="min-w-0 flex-1 overflow-hidden">
          <StandingsStrip state={state} showRoundCounter />
        </div>
        <ConnectionStatus quality={quality} compact />
      </header>

      <div
        className={`flex flex-1 flex-col min-[900px]:grid min-[900px]:grid-cols-[1fr_300px] min-[900px]:gap-0 min-[900px]:p-0 ${
          covered ? 'gap-[10px] px-3 pt-2' : 'gap-5 p-4'
        }`}
      >
        <main
          className={`flex flex-col items-center min-[900px]:justify-center min-[900px]:gap-7 min-[900px]:p-7 ${
            covered ? 'gap-[10px]' : 'gap-5'
          }`}
        >
          {covered ? (
            // Both clocks in one line: the window that is running out, and the
            // round clock holding while it does. Its own test id, because the
            // disc row's rule is that it always has three slots and this is not
            // that row.
            <div data-testid="clock-row-compact" className="flex w-full items-center gap-[10px]">
              <CompactClock
                remaining={buzzWindow.remaining}
                seconds={buzzWindow.seconds}
                variant="buzz"
                label={`${buzzWindow.seconds} seconds to answer`}
              />
              <span className="label-mono shrink-0">{`round held at ${main.seconds}`}</span>
            </div>
          ) : (
            <div data-testid="clock-row" className="flex items-center justify-center gap-5">
              {/* Mirrors the held-clock slot, so the main disc is centred with a
                  buzz live and without one, and never slides across when one
                  lands. */}
              <span className="w-[88px] shrink-0" aria-hidden="true" />

              {/* One element in both roles rather than two that swap: the disc
                  keeps its DOM node, so a buzz changes what it counts without
                  remounting the clock under the player. */}
              {/* Still one element in both roles — swapping which CLOCK it is
                  handed keeps the component instance, and with it the DOM node.
                  `main` and `buzzWindow` above are the same arithmetic at four
                  times a second, for the labels and the variant. */}
              <LiveClock
                clock={(youHoldTheBuzz ? round.buzzClock : round.clock) ?? null}
                frozen={
                  youHoldTheBuzz ? state.serverPaused : state.serverPaused || someoneElseBuzzed
                }
                size={clockSize}
                variant={
                  youHoldTheBuzz ? 'buzz' : main.frozen ? 'frozen' : main.urgent ? 'alert' : 'normal'
                }
                label={
                  youHoldTheBuzz
                    ? `${buzzWindow.seconds} seconds to answer`
                    : someoneElseBuzzed
                      ? `the clock is paused at ${main.seconds} seconds while somebody answers`
                      : `${main.seconds} seconds left`
                }
              />

              <div className="flex w-[88px] shrink-0 flex-col items-center gap-2">
                {youHoldTheBuzz && (
                  <div className="fade-in flex flex-col items-center gap-2">
                    {/* Frozen by definition — the server pauses the round clock
                        on a buzz — so this one asks for no frames either way.
                        `LiveClock` for the same reason the disc above uses it:
                        one way of drawing a clock on this screen. */}
                    <LiveClock
                      clock={round.clock ?? null}
                      frozen
                      size={56}
                      variant="frozen"
                      label={`the round clock is holding at ${main.seconds} seconds`}
                    />
                    <span className="label-mono text-center">the round clock is holding</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Nine tiles, always: a conundrum is nine letters by definition, and
              nine `board` tiles are wider than this column at 900px. The row
              scales to what it has rather than overflowing — see `tileFitStyle`
              — and the gap comes from the same breakpoint the tile size does, so
              the formula and the layout cannot disagree. */}
          <div className="tile-fit w-full">
            <div
              data-board
              className="flex justify-center"
              style={{ gap: boardGap, ...tileFitStyle(tileSize, letters.length, boardGap) }}
            >
              {letters.map((letter, index) => (
                <LetterTile
                  key={index}
                  letter={letter}
                  state={
                    youAreLockedOut
                      ? 'locked'
                      : youHoldTheBuzz && spentTiles.has(index)
                        ? 'used'
                        : 'available'
                  }
                  size={tileSize}
                  fit
                />
              ))}
            </div>
          </div>

          {/* A floor rather than a fixed height: every state that arrives from
              somebody else's action fits inside it, so the board above stays
              put. Only the form, which a player opens by their own buzz, is
              taller. */}
          <div className="flex w-full min-h-[148px] flex-col items-center justify-start min-[900px]:w-[520px]">
            {youHoldTheBuzz ? (
              <form
                className="fade-in flex w-full flex-col gap-[10px]"
                onSubmit={(event) => {
                  event.preventDefault();
                  submit();
                }}
              >
                <label className="label-mono" htmlFor="conundrum-answer">
                  your answer
                </label>
                <input
                  id="conundrum-answer"
                  // The one input in the room, so it takes focus without being asked.
                  autoFocus
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="the nine letter word"
                  aria-describedby="conundrum-buildable"
                  className="h-[60px] rounded-card border-2 border-accent bg-surface px-4 text-[28px] font-extrabold tracking-[0.06em] uppercase text-ink placeholder:text-[15px] placeholder:font-normal placeholder:tracking-normal placeholder:text-ink-dim"
                />
                {/* The slot is always here, saying nothing most of the time. A
                    line that appeared would push the submit button down the
                    moment a player mistyped, under a thumb already moving. No
                    live region: this changes on every keystroke, and announcing
                    each one is worse than silence. */}
                <p
                  id="conundrum-buildable"
                  className={`min-h-[16px] text-[12px] leading-[16px] ${
                    spent.unmatched > 0 ? 'text-alert' : 'text-ink-dim'
                  }`}
                >
                  {spent.unmatched === 0
                    ? ''
                    : spent.unmatched === 1
                      ? 'one of those letters is not on the board'
                      : `${spent.unmatched} of those letters are not on the board`}
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  block
                  type="submit"
                  // Refused rather than merely discouraged. A word the board
                  // cannot spell is certainly not the anagram, and submitting
                  // one here does not cost a nought, it costs the rest of the
                  // round — one wrong answer is a lockout.
                  disabled={draft.trim().length === 0 || spent.unmatched > 0}
                >
                  Submit answer
                </Button>
                {/* The held clock beside the disc already says the round is
                    waiting, and says it with a number. This line is for the
                    thing nothing else on the screen says. */}
                <p className="text-[13px] leading-normal text-ink-muted">
                  A wrong word puts you out of this conundrum.
                </p>
              </form>
            ) : youAreLockedOut ? (
              <div className="fade-in flex w-full flex-col items-center gap-2 rounded-card border border-line bg-surface-2 px-4 py-5">
                <span className="text-ink-dim">
                  <Padlock size={26} />
                </span>
                {/* A line saying the others are still playing it out followed
                    this. The clock is on the screen above, still running, with
                    everybody's state in the rail beside it. */}
                <span className="text-[15px] font-semibold">
                  You answered wrong, so you are out for this conundrum.
                </span>
              </div>
            ) : someoneElseBuzzed ? (
              <div className="fade-in flex w-full flex-col items-center gap-3 rounded-card border border-accent bg-accent/15 px-4 py-5">
                <div className="flex items-center gap-2">
                  {buzzer !== null && <PlayerChip player={buzzer} size="md" />}
                  <span className="text-[15px] font-semibold">buzzed in</span>
                </div>
                {/* The one thing on this frame the frozen disc does not say, and
                    worded so it can never be read as the server-pause overlay. */}
                <span className="text-[13px] text-ink-muted">
                  The clock holds until that answer lands.
                </span>
              </div>
            ) : solvedBy !== null ? (
              <div className="fade-in flex w-full flex-col items-center gap-2 rounded-card border border-good bg-good/15 px-4 py-5">
                <span className="text-[15px] font-semibold">
                  {`${solver?.displayName ?? 'Somebody'} solved it.`}
                </span>
              </div>
            ) : (
              <div className="flex w-full flex-col gap-[10px]">
                <Button variant="primary" size="xl" block disabled={!canBuzz} onClick={buzz}>
                  Buzz
                </Button>
                {/* "one answer each" used to sit beside the points. The rule it
                    states is the lockout, which the buzzer's own form states
                    once, where it is about to cost something. */}
                <div className="flex items-center justify-between gap-3 font-mono text-xs font-medium text-ink-dim">
                  <span>{`worth ${CONUNDRUM_POINTS}`}</span>
                  <span className="hidden min-[900px]:inline">space bar buzzes</span>
                </div>
                {!youArePlaying && (
                  <p className="text-[13px] text-ink-dim">Spectators watch this one.</p>
                )}
              </div>
            )}
          </div>
        </main>

        <aside className="mt-auto flex flex-col gap-[10px] min-[900px]:mt-0 min-[900px]:min-h-0 min-[900px]:gap-0 min-[900px]:border-l min-[900px]:border-line">
          <div className="flex flex-col gap-[10px] min-[900px]:border-b min-[900px]:border-line min-[900px]:p-[18px]">
            <div className="label-mono">players</div>
            {/* The lockout rule was stated under these rows as well, about
                other people, on a screen that already says it to the one player
                it can cost anything. `locked out` on a row says the rest. */}
            <div className="flex gap-2 min-[900px]:flex-col">
              {roster.map((player) => (
                <PlayerRow key={player.id} player={player} status={statusOf(player)} />
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
